import {
  ArrowRight,
  Eye,
  FileText,
  Link2,
  Loader2,
  Plus,
  PowerOff,
  Search,
  ShieldAlert,
  Webhook,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useGetIntakesQuery } from '@/services/api/intakeService';
import {
  type CreateLeadIntegrationConnectionPayload,
  type Lead,
  type LeadDetail,
  type LeadIntegrationConnection,
  type LeadStatus,
  useCreateConnectionMutation,
  useCreateIntakeDraftMutation,
  useCreateSubscriptionMutation,
  useDeactivateConnectionMutation,
  useGetConnectionsQuery,
  useGetLeadQuery,
  useGetLeadsQuery,
  useLinkIntakeMutation,
  useUpdateLeadStatusMutation,
} from '@/services/api/leadIntegrationService';

const BACKEND_HOST =
  import.meta.env.VITE_APP_DJANGO_BACKEND_HOST || 'http://localhost:8000';

const STATUS_COLORS: Record<LeadStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  reviewing: 'bg-amber-100 text-amber-800',
  intake_created: 'bg-emerald-100 text-emerald-800',
  converted: 'bg-violet-100 text-violet-800',
  rejected: 'bg-red-100 text-red-800',
  closed: 'bg-gray-100 text-gray-800',
  error: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  reviewing: 'Evaluating',
  intake_created: 'Intake Created',
  converted: 'Converted',
  rejected: 'Rejected',
  closed: 'Closed',
  error: 'Error',
};

const PROVIDER_LABELS: Record<string, string> = {
  lexamica: 'Lexamica',
};

const INTAKE_STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress',
  sent_for_signature: 'Sent for Signature',
  signed: 'Signed',
  archived: 'Archived',
  rejected: 'Rejected',
  chase: 'Chase',
  lost: 'Lost',
  escalated: 'Escalated',
};

const INTAKE_STATUS_COLORS: Record<string, string> = {
  in_progress: 'bg-blue-100 text-blue-800',
  sent_for_signature: 'bg-yellow-100 text-yellow-800',
  signed: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
  rejected: 'bg-red-100 text-red-800',
  chase: 'bg-orange-100 text-orange-800',
  lost: 'bg-gray-100 text-gray-800',
  escalated: 'bg-purple-100 text-purple-800',
};

function formatDate(iso?: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateOnly(value?: string | null): string {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

function getNestedValue(
  value: Record<string, unknown> | undefined,
  path: string[],
): unknown {
  return path.reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, value);
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

const DetailValue = ({
  value,
  emptyText = 'Not available',
}: {
  value: unknown;
  emptyText?: string;
}) => {
  if (isBlank(value)) {
    return <span className="text-gray-400">{emptyText}</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }

  return <span className="break-words">{String(value)}</span>;
};

const DetailGrid = ({
  fields,
}: {
  fields: Array<{ label: string; value: unknown; emptyText?: string }>;
}) => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    {fields.map((field) => (
      <div key={field.label} className="rounded-lg border border-gray-100 bg-gray-50/70 p-3">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {field.label}
        </div>
        <div className="mt-1 text-sm text-gray-900">
          <DetailValue value={field.value} emptyText={field.emptyText} />
        </div>
      </div>
    ))}
  </div>
);

const LeadDetailsDialog = ({
  leadId,
  open,
  onOpenChange,
}: {
  leadId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data, isLoading } = useGetLeadQuery(leadId ?? '', {
    skip: !leadId || !open,
  });

  const detail = data as LeadDetail | undefined;
  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  } else if (!detail) {
    content = (
      <div className="rounded-lg border border-dashed p-8 text-sm text-gray-500">
        Lead details unavailable.
      </div>
    );
  } else {
    const normalized = detail.lead.normalized_payload ?? {};
    const proposed = detail.lead.proposed_intake_payload ?? {};
    const lexamicaMeta = detail.lexamica_metadata;

    const clientFields = [
      {
        label: 'Full Name',
        value: normalized.client_full_name,
      },
      {
        label: 'Phone',
        value: normalized.client_phone,
      },
      {
        label: 'Email',
        value: normalized.client_email,
      },
      {
        label: 'Address',
        value: normalized.client_address,
      },
      {
        label: 'Date of Birth',
        value: formatDateOnly(normalized.client_dob),
        emptyText: 'Not provided',
      },
      {
        label: 'Live Transfer Requested',
        value: detail.lead.live_transfer_requested,
      },
    ];

    const incidentFields = [
      {
        label: 'Incident Date',
        value: formatDateOnly(normalized.incident_date || lexamicaMeta?.incident_date_raw),
        emptyText: 'Not provided',
      },
      {
        label: 'Incident State',
        value: normalized.incident_state || lexamicaMeta?.incident_state,
      },
      {
        label: 'Incident Location',
        value: normalized.incident_location,
      },
      {
        label: 'Incident Type',
        value: normalized.incident_type,
      },
      {
        label: 'Case Type',
        value: normalized.case_type || lexamicaMeta?.case_type,
      },
      {
        label: 'Summary / Description',
        value: normalized.incident_description || normalized.notes,
      },
    ];

    const intakeFields = [
      {
        label: 'Lead Source',
        value: getNestedValue(proposed, ['lead_source']),
      },
      {
        label: 'Intake Full Name',
        value: getNestedValue(proposed, ['personal_info', 'full_name']),
      },
      {
        label: 'Intake Phone',
        value: getNestedValue(proposed, ['personal_info', 'phone']),
      },
      {
        label: 'Intake Email',
        value: getNestedValue(proposed, ['personal_info', 'email']),
      },
      {
        label: 'Intake Incident Date',
        value: formatDateOnly(
          getNestedValue(proposed, ['incident_info', 'date']) as string | null | undefined,
        ),
        emptyText: 'Not provided',
      },
      {
        label: 'Intake Incident State',
        value: getNestedValue(proposed, ['incident_info', 'state']),
      },
    ];

    const missingFields = [
      { label: 'Client full name', value: normalized.client_full_name },
      {
        label: 'Client phone or email',
        value: normalized.client_phone || normalized.client_email,
      },
      { label: 'Incident date', value: normalized.incident_date },
      { label: 'Incident state', value: normalized.incident_state },
      { label: 'Incident location', value: normalized.incident_location },
      {
        label: 'Incident description',
        value: normalized.incident_description || normalized.notes,
      },
      { label: 'Case type', value: normalized.case_type || lexamicaMeta?.case_type },
      { label: 'Client address', value: normalized.client_address },
      { label: 'Client date of birth', value: normalized.client_dob },
    ].filter((field) => isBlank(field.value));

    content = (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Status</CardDescription>
              <CardTitle className="text-base">
                {STATUS_LABELS[detail.lead.status] ?? detail.lead.status}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Provider</CardDescription>
              <CardTitle className="text-base">
                {providerLabel(detail.lead.provider)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>External Lead ID</CardDescription>
              <CardTitle className="text-base break-all">
                {detail.lead.external_lead_id}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Received</CardDescription>
              <CardTitle className="text-base">
                {formatDate(detail.lead.created_at)}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Client Details</CardTitle>
            <CardDescription>
              Key client information stored on the lead record.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DetailGrid fields={clientFields} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Incident Details
            </CardTitle>
            <CardDescription>
              Normalized incident information prepared for intake review.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DetailGrid fields={incidentFields} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Missing / Needs Review</CardTitle>
            <CardDescription>
              Important fields that are still blank in the normalized lead record.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {missingFields.length === 0 ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                No obvious key fields are missing from the normalized lead data.
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="text-sm font-medium text-amber-900">
                  Review these fields before intake conversion:
                </div>
                <ul className="mt-3 space-y-2 text-sm text-amber-900">
                  {missingFields.map((field) => (
                    <li key={field.label} className="flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-700" />
                      <span>{field.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Proposed Intake Preview</CardTitle>
            <CardDescription>
              The structured intake payload that will be used when creating a draft.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DetailGrid fields={intakeFields} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Raw Payload</CardTitle>
            <CardDescription>
              Debug snapshot from the stored provider payload.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <details className="rounded-md border">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700">
                Show raw JSON
              </summary>
              <div className="border-t">
                <pre className="overflow-x-auto bg-slate-950 p-4 text-xs text-slate-50">
                  {JSON.stringify(detail.lead.raw_payload, null, 2)}
                </pre>
              </div>
            </details>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lead Details</DialogTitle>
          <DialogDescription>
            Review the stored lead record, provider context, and any missing fields.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}

const RejectLeadDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Lead</DialogTitle>
          <DialogDescription>
            Add an optional reason to send back to the lead provider.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="lead-reject-reason">Reason</Label>
          <Textarea
            id="lead-reject-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Conflict, outside jurisdiction, duplicate intake..."
            rows={5}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              onConfirm(reason).catch(() => undefined);
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Rejecting...
              </>
            ) : (
              'Reject Lead'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const INTAKE_SEARCH_DEBOUNCE_MS = 300;

const LinkIntakeDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (intakeId: string) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIntakeId, setSelectedIntakeId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setDebouncedSearch('');
      setSelectedIntakeId(null);
    }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, INTAKE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setSelectedIntakeId(null);
  }, [debouncedSearch]);

  const { data: intakesData, isFetching } = useGetIntakesQuery(
    { search: debouncedSearch, limit: 20, offset: 0 },
    { skip: !open || debouncedSearch.length < 2 },
  );

  const intakes = intakesData?.results ?? [];

  const handleSelect = useCallback((intakeId: string) => {
    setSelectedIntakeId((prev) => (prev === intakeId ? null : intakeId));
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link to Intake</DialogTitle>
          <DialogDescription>
            Search for an existing intake by client name to link to this lead.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="link-intake-search">Search Intakes</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                id="link-intake-search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by client name, phone, or email..."
                className="pl-9"
              />
            </div>
          </div>

          <div className="max-h-64 min-h-[120px] overflow-y-auto rounded-md border">
            {debouncedSearch.length < 2 ? (
              <div className="flex h-[120px] items-center justify-center text-sm text-gray-500">
                Type at least 2 characters to search
              </div>
            ) : isFetching ? (
              <div className="flex h-[120px] items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : intakes.length === 0 ? (
              <div className="flex h-[120px] items-center justify-center text-sm text-gray-500">
                No intakes found
              </div>
            ) : (
              <div className="divide-y">
                {intakes.map((intake) => (
                  <button
                    key={intake.id}
                    type="button"
                    className={`w-full px-3 py-2.5 text-left transition-colors hover:bg-gray-50 ${
                      selectedIntakeId === intake.id
                        ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
                        : ''
                    }`}
                    onClick={() => handleSelect(intake.id)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {intake.client_name || 'Unknown'}
                      </span>
                      <Badge
                        className={`text-xs ${
                          intake.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : intake.status === 'signed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {intake.status}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex gap-3 text-xs text-gray-500">
                      <span>{intake.client_phone || intake.incident_type || '-'}</span>
                      <span>{formatDate(intake.created_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedIntakeId) {
                onConfirm(selectedIntakeId).catch(() => undefined);
              }
            }}
            disabled={isSubmitting || !selectedIntakeId}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Linking...
              </>
            ) : (
              'Link Intake'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LeadRow = ({ lead }: { lead: Lead }) => {
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [linkIntakeOpen, setLinkIntakeOpen] = useState(false);
  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateLeadStatusMutation();
  const [createDraft, { isLoading: isCreatingDraft }] =
    useCreateIntakeDraftMutation();
  const [linkIntake, { isLoading: isLinking }] = useLinkIntakeMutation();

  const normalized = lead.normalized_payload;
  const statusColor = STATUS_COLORS[lead.status] ?? 'bg-gray-100 text-gray-800';
  const statusLabel = STATUS_LABELS[lead.status] ?? lead.status;
  const canCreateIntake =
    !lead.intake_id &&
    (lead.status === 'new' || lead.status === 'reviewing');
  const canReject = lead.status === 'new' || lead.status === 'reviewing';
  const canStartReview = lead.status === 'new';
  const canViewIntake = Boolean(lead.intake_id);
  const canLinkIntake = !lead.intake_id;
  const canChangeIntake = Boolean(lead.intake_id);

  const handleStartReview = async () => {
    try {
      await updateStatus({ id: lead.id, status: 'reviewing' }).unwrap();
      toast.success('Lead set to evaluating');
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ||
        'Failed to set lead to evaluating — outbound call to provider may have failed';
      toast.error(message);
    }
  };

  const handleCreateDraft = async () => {
    try {
      const result = await createDraft(lead.id).unwrap();
      const intakeId = result.lead.intake_id;
      if (!intakeId) {
        toast.error('Lead was accepted, but no intake was returned');
        return;
      }
      toast.success('Intake draft created');
      navigate(`/intakes/${intakeId}`);
    } catch {
      toast.error('Failed to create intake draft');
    }
  };

  const handleReject = async (reason: string) => {
    try {
      await updateStatus({
        id: lead.id,
        status: 'rejected',
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }).unwrap();
      toast.success('Lead rejected');
      setRejectOpen(false);
    } catch {
      toast.error('Failed to reject lead');
    }
  };

  const handleLinkIntake = async (intakeId: string) => {
    try {
      await linkIntake({ leadId: lead.id, intakeId }).unwrap();
      toast.success('Intake linked to lead');
      setLinkIntakeOpen(false);
    } catch (err) {
      const message =
        (err as { data?: { error?: string } })?.data?.error ||
        'Failed to link intake';
      toast.error(message);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell className="align-top">
          <div className="font-medium">
            {normalized.client_full_name || 'Unknown'}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            {normalized.client_phone || normalized.client_email || '-'}
          </div>
        </TableCell>
        <TableCell className="align-top">{providerLabel(lead.provider)}</TableCell>
        <TableCell className="align-top text-sm text-gray-700">
          {lead.referring_firm_name || '-'}
        </TableCell>
        <TableCell className="align-top">
          <div>{normalized.case_type || '-'}</div>
          <div className="mt-1 text-xs text-gray-500">
            {normalized.incident_type || '-'}
          </div>
        </TableCell>
        <TableCell className="align-top">
          <div>{normalized.incident_state || '-'}</div>
          <div className="mt-1 text-xs text-gray-500">
            {normalized.incident_date || '-'}
          </div>
        </TableCell>
        <TableCell className="align-top">
          <Badge className={statusColor}>{statusLabel}</Badge>
        </TableCell>
        <TableCell className="align-top">
          {lead.intake_status ? (
            <Badge className={INTAKE_STATUS_COLORS[lead.intake_status] ?? 'bg-gray-100 text-gray-800'}>
              {INTAKE_STATUS_LABELS[lead.intake_status] ?? lead.intake_status}
            </Badge>
          ) : (
            <span className="text-sm text-gray-500">-</span>
          )}
        </TableCell>
        <TableCell className="align-top text-gray-500">
          {formatDate(lead.created_at)}
        </TableCell>
        <TableCell className="align-top">
          <div className="flex flex-wrap gap-2">
            {canStartReview && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  handleStartReview().catch(() => undefined);
                }}
                disabled={isUpdating || isCreatingDraft || isLinking}
              >
                {isUpdating ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Search className="mr-1 h-3 w-3" />
                )}
                Set to Evaluating
              </Button>
            )}

            {canCreateIntake && (
              <Button
                size="sm"
                onClick={() => {
                  handleCreateDraft().catch(() => undefined);
                }}
                disabled={isUpdating || isCreatingDraft || isLinking}
              >
                {isCreatingDraft ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <FileText className="mr-1 h-3 w-3" />
                )}
                Create Intake
              </Button>
            )}

            {canViewIntake && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/intakes/${lead.intake_id}`)}
              >
                <ArrowRight className="mr-1 h-3 w-3" />
                View Intake
              </Button>
            )}

            {canChangeIntake && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLinkIntakeOpen(true)}
                disabled={isUpdating || isCreatingDraft || isLinking}
              >
                <Link2 className="mr-1 h-3 w-3" />
                Change Intake
              </Button>
            )}

            {canLinkIntake && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLinkIntakeOpen(true)}
                disabled={isUpdating || isCreatingDraft || isLinking}
              >
                <Link2 className="mr-1 h-3 w-3" />
                Link to Intake
              </Button>
            )}

            {canReject && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejectOpen(true)}
                disabled={isUpdating || isCreatingDraft || isLinking}
              >
                <XCircle className="mr-1 h-3 w-3" />
                Reject
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDetailsOpen(true)}
            >
              <Eye className="mr-1 h-3 w-3" />
              Details
            </Button>
          </div>
        </TableCell>
      </TableRow>

      <LeadDetailsDialog
        leadId={lead.id}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />

      <RejectLeadDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={handleReject}
        isSubmitting={isUpdating}
      />

      <LinkIntakeDialog
        open={linkIntakeOpen}
        onOpenChange={setLinkIntakeOpen}
        onConfirm={handleLinkIntake}
        isSubmitting={isLinking}
      />
    </>
  );
}

const NewConnectionDialog = ({
  firmId,
  open,
  onOpenChange,
}: {
  firmId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [provider, setProvider] = useState('lexamica');
  const [externalAccountId, setExternalAccountId] = useState('');
  const [authConfigRef, setAuthConfigRef] = useState('');
  const [webhookSecretRef, setWebhookSecretRef] = useState('');
  const [privateKeyRef, setPrivateKeyRef] = useState('');
  const [createConnection, { isLoading }] = useCreateConnectionMutation();

  useEffect(() => {
    if (!open) {
      setProvider('lexamica');
      setExternalAccountId('');
      setAuthConfigRef('');
      setWebhookSecretRef('');
      setPrivateKeyRef('');
    }
  }, [open]);

  const handleCreate = async () => {
    if (!externalAccountId.trim() || !authConfigRef.trim() || !webhookSecretRef.trim()) {
      toast.error('Org/account ID, auth ref, and webhook secret ref are required');
      return;
    }

    const payload: CreateLeadIntegrationConnectionPayload = {
      provider,
      firm_id: firmId,
      external_account_id: externalAccountId.trim(),
      auth_config_ref: authConfigRef.trim(),
      webhook_secret_ref: webhookSecretRef.trim(),
      private_key_ref: privateKeyRef.trim() || null,
    };

    try {
      await createConnection(payload).unwrap();
      toast.success('Lead connection created');
      onOpenChange(false);
    } catch {
      toast.error('Failed to create lead connection');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Lead Connection</DialogTitle>
          <DialogDescription>
            Configure a provider connection for this firm.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lead-provider">Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger id="lead-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lexamica">Lexamica</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-external-account-id">Org / Account ID</Label>
            <Input
              id="lead-external-account-id"
              value={externalAccountId}
              onChange={(event) => setExternalAccountId(event.target.value)}
              placeholder="organization-id"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-auth-ref">Auth Config Ref</Label>
            <Input
              id="lead-auth-ref"
              value={authConfigRef}
              onChange={(event) => setAuthConfigRef(event.target.value)}
              placeholder="ssm:/integrations/lexamica/public-key"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-webhook-secret-ref">Webhook Secret Ref</Label>
            <Input
              id="lead-webhook-secret-ref"
              value={webhookSecretRef}
              onChange={(event) => setWebhookSecretRef(event.target.value)}
              placeholder="ssm:/integrations/lexamica/webhook-secret"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-private-key-ref">Private Key Ref</Label>
            <Input
              id="lead-private-key-ref"
              value={privateKeyRef}
              onChange={(event) => setPrivateKeyRef(event.target.value)}
              placeholder="ssm:/integrations/lexamica/private-key"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={() => handleCreate().catch(() => undefined)} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Connection'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CreateSubscriptionDialog = ({
  connection,
  open,
  onOpenChange,
}: {
  connection: LeadIntegrationConnection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [eventName, setEventName] = useState('Case Received');
  const [webhookUrl, setWebhookUrl] = useState(
    `${BACKEND_HOST}/api/integrations/lexamica/webhooks/`,
  );
  const [createSubscription, { isLoading }] = useCreateSubscriptionMutation();

  useEffect(() => {
    if (!open) {
      setEventName('Case Received');
      setWebhookUrl(`${BACKEND_HOST}/api/integrations/lexamica/webhooks/`);
    }
  }, [open]);

  const handleCreate = async () => {
    if (!connection) return;
    if (!webhookUrl.trim()) {
      toast.error('Webhook URL is required');
      return;
    }

    try {
      const result = await createSubscription({
        id: connection.id,
        webhook_url: webhookUrl.trim(),
        event: eventName.trim() || 'Case Received',
      }).unwrap();
      toast.success(`Webhook subscription created: ${result.subscription_id}`);
      onOpenChange(false);
    } catch {
      toast.error('Failed to create webhook subscription');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Webhook Subscription</DialogTitle>
          <DialogDescription>
            Register the provider webhook subscription for this connection. The
            Lexamica Case Invitation mapping is resolved from environment config.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            <div className="font-medium">{providerLabel(connection?.provider ?? 'lexamica')}</div>
            <div className="mt-1 text-blue-700">
              Account: {connection?.external_account_id ?? '-'}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-subscription-event">Provider Event</Label>
            <Input
              id="lead-subscription-event"
              value={eventName}
              onChange={(event) => setEventName(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lead-subscription-webhook-url">Webhook URL</Label>
            <Input
              id="lead-subscription-webhook-url"
              value={webhookUrl}
              onChange={(event) => setWebhookUrl(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={() => handleCreate().catch(() => undefined)} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Subscription'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const LeadIntegrationsPanel = ({
  firmId,
}: {
  firmId?: string;
}) => {
  const isAllFirmsView = !firmId;
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [isNewConnectionOpen, setIsNewConnectionOpen] = useState(false);
  const [subscriptionConnection, setSubscriptionConnection] =
    useState<LeadIntegrationConnection | null>(null);
  const [deactivateConnection, { isLoading: isDeactivating }] =
    useDeactivateConnectionMutation();

  const {
    data: connections = [],
    isLoading: isLoadingConnections,
  } = useGetConnectionsQuery(firmId ?? '', {
    skip: !firmId,
  });

  const { data: leads = [], isLoading: isLoadingLeads } = useGetLeadsQuery(
    {
      ...(firmId ? { firm_id: firmId } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(providerFilter !== 'all' ? { provider: providerFilter } : {}),
    },
  );

  const providers = useMemo(() => {
    const values = new Set<string>();
    connections.forEach((connection) => values.add(connection.provider));
    leads.forEach((lead) => values.add(lead.provider));
    return Array.from(values.values()).sort();
  }, [connections, leads]);

  const handleDeactivateConnection = async (connection: LeadIntegrationConnection) => {
    try {
      await deactivateConnection(connection.id).unwrap();
      toast.success('Lead connection deactivated');
    } catch {
      toast.error('Failed to deactivate lead connection');
    }
  };

  let connectionContent: React.ReactNode;
  if (isLoadingConnections) {
    connectionContent = (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  } else if (connections.length === 0) {
    connectionContent = (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-gray-500">
        No lead connections configured yet.
      </div>
    );
  } else {
    connectionContent = (
      <div className="space-y-4">
        {connections.map((connection) => (
          <div
            key={connection.id}
            className="rounded-lg border px-4 py-4"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-medium text-gray-900">
                    {providerLabel(connection.provider)}
                  </div>
                  <Badge
                    className={
                      connection.active
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-100 text-gray-700'
                    }
                  >
                    {connection.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="text-sm text-gray-600">
                  Account ID: {connection.external_account_id}
                </div>
                <div className="text-sm text-gray-600">
                  Subscription ID:{' '}
                  {connection.webhook_subscription_id ?? 'Not created'}
                </div>
                <div className="text-xs text-gray-500">
                  Created {formatDate(connection.created_at)}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {connection.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSubscriptionConnection(connection)}
                  >
                    <Webhook className="mr-1 h-3.5 w-3.5" />
                    {connection.webhook_subscription_id
                      ? 'Replace Subscription'
                      : 'Create Subscription'}
                  </Button>
                )}

                {connection.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      handleDeactivateConnection(connection).catch(
                        () => undefined,
                      );
                    }}
                    disabled={isDeactivating}
                  >
                    {isDeactivating ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <PowerOff className="mr-1 h-3.5 w-3.5" />
                    )}
                    Deactivate
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  let leadContent: React.ReactNode;
  if (isLoadingLeads) {
    leadContent = (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  } else if (leads.length === 0) {
    leadContent = (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <ShieldAlert className="h-5 w-5 text-gray-500" />
        </div>
        <div className="mt-4 text-sm font-medium text-gray-900">
          No leads found
        </div>
        <div className="mt-1 text-sm text-gray-500">
          Leads will appear here once your provider connection starts sending events.
        </div>
      </div>
    );
  } else {
    leadContent = (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Referring Firm</TableHead>
              <TableHead>Case</TableHead>
              <TableHead>Incident</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Intake Status</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => (
              <LeadRow key={lead.id} lead={lead} />
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {isAllFirmsView ? 'Leads' : 'Lead Integrations'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isAllFirmsView
              ? 'Review incoming leads across all firms.'
              : 'Manage inbound lead provider connections and review incoming leads for this firm.'}
          </p>
        </div>
        {!isAllFirmsView && (
          <Button onClick={() => setIsNewConnectionOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Connection
          </Button>
        )}
      </div>

      {!isAllFirmsView && (
        <Card>
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle>Provider Connections</CardTitle>
              <CardDescription>
                Active connections receive inbound webhooks and can create provider subscriptions.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {connectionContent}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="gap-4">
          <div>
            <CardTitle>Lead Queue</CardTitle>
            <CardDescription>
              {isAllFirmsView
                ? 'Review, accept, and reject inbound leads across all firms.'
                : 'Review, accept, and reject inbound leads from configured providers.'}
            </CardDescription>
          </div>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2">
              {(
                [
                  'all',
                  'new',
                  'reviewing',
                  'intake_created',
                  'converted',
                  'rejected',
                  'closed',
                  'error',
                ] as const
              ).map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={statusFilter === status ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(status)}
                >
                  {status === 'all' ? 'All' : STATUS_LABELS[status]}
                </Button>
              ))}
            </div>

            <div className="w-full xl:w-56">
              <Select value={providerFilter} onValueChange={setProviderFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All providers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All providers</SelectItem>
                  {providers.map((provider) => (
                    <SelectItem key={provider} value={provider}>
                      {providerLabel(provider)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {leadContent}
        </CardContent>
      </Card>

      {!isAllFirmsView && (
        <NewConnectionDialog
          firmId={firmId}
          open={isNewConnectionOpen}
          onOpenChange={setIsNewConnectionOpen}
        />
      )}

      <CreateSubscriptionDialog
        connection={subscriptionConnection}
        open={Boolean(subscriptionConnection)}
        onOpenChange={(open) => {
          if (!open) {
            setSubscriptionConnection(null);
          }
        }}
      />
    </div>
  );
}
